/** Workflow queries and the act mutation shared by every approval panel. */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useRefetchInterval } from "@/hooks/useRealtime";
import { api } from "@/lib/api";
import type { InboxItem, Page, WorkflowState } from "@/lib/types";

export function useInbox(params?: { entity_type?: string; overdue_only?: boolean }) {
  // Approval queues change because of other people. The event stream says so the
  // moment it happens, so the timer here is only what covers a stream that is
  // down - see `useRefetchInterval`.
  const refetchInterval = useRefetchInterval(60_000);
  return useQuery({
    queryKey: ["inbox", params],
    queryFn: () =>
      api
        .get<Page<InboxItem>>("/v1/workflow/inbox", { ...params, page_size: 100 })
        .then((page) => page.items),
    refetchInterval,
    staleTime: 20_000,
  });
}

export function useInboxSummary() {
  const refetchInterval = useRefetchInterval(60_000);
  return useQuery({
    queryKey: ["inbox", "summary"],
    queryFn: () =>
      api.get<{ total: number; overdue: number; by_entity: Record<string, number> }>(
        "/v1/workflow/inbox/summary",
      ),
    refetchInterval,
  });
}

export function useWorkflowState(entityType: string, entityId: string | undefined) {
  return useQuery({
    queryKey: ["workflow", entityType, entityId],
    queryFn: () => api.get<WorkflowState>(`/v1/workflow/${entityType}/${entityId}/state`),
    enabled: Boolean(entityId),
  });
}

export interface ActPayload {
  decision:
    | "approved"
    | "rejected"
    | "returned"
    | "commented"
    | "cancelled"
    | "on_hold"
    | "resumed";
  comment?: string;
  target_stage_id?: string;
  /** Which of the stage's confirmations were ticked. Affirmative decisions only. */
  checklist?: { key: string; checked: boolean }[];
}

export function useWorkflowAct(entityType: string, entityId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ActPayload) =>
      api.post<WorkflowState>(`/v1/workflow/${entityType}/${entityId}/act`, payload),
    onSuccess: (state) => {
      queryClient.setQueryData(["workflow", entityType, entityId], state);
      // Acting changes the record, the inbox and usually a dashboard number.
      void queryClient.invalidateQueries({ queryKey: ["inbox"] });
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      void queryClient.invalidateQueries({ queryKey: [entityType] });
      void queryClient.invalidateQueries({ queryKey: ["analyses"] });
      void queryClient.invalidateQueries({ queryKey: ["requests"] });
      void queryClient.invalidateQueries({ queryKey: ["certificates"] });
    },
  });
}
